import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  Info,
  CheckCircle2,
} from "lucide-react";
import { imgNbaLogo } from "../../../../assets";

export default function KYCStep7() {
  const navigate = useNavigate();
  // 'full' or 'installment'
  const [paymentType, setPaymentType] = useState<"full" | "installment" | null>(
    null
  );

  const handleContinue = () => {
    // Navigate to Success page regardless of choice for now,
    // or we could add logic to handle installment flow differently if needed.
    // Based on "Do all that needs doing", we assume this finalizes the transaction structure.
    if (paymentType) {
      navigate("/d/kyc/success");
    }
  };

  return (
    <div className="kyc-page">
      <div className="kyc-header">
        <div className="kyc-logo-section">
          <img className="kyc-logo-img" src={imgNbaLogo} alt="NBA Logo" />
          <div className="kyc-logo-text">
            <div className="kyc-logo-name">NIGERIAN BAR ASSOCIATION</div>
            <div className="kyc-logo-sub">REMUNERATION PORTAL</div>
          </div>
        </div>
      </div>

      <div className="kyc-progress">
        <button
          className="kyc-back-button"
          type="button"
          onClick={() => navigate("/d/kyc/step6")}
          aria-label="Go back"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="kyc-progress-content">
          <span className="kyc-progress-text">Step 7 of 7</span>
          <div className="kyc-progress-bar">
            <div className="kyc-progress-fill" style={{ width: "100%" }} />
          </div>
        </div>
      </div>

      <div className="kyc-container">
        <div className="kyc-content">
          <h1 className="kyc-title">Select Transaction Type</h1>
          <p className="kyc-subtitle">
            Choose how you would like to structure this transaction
          </p>

          <div className="space-y-4 mt-8">
            {/* Full Payment Option */}
            <button
              className={`w-full flex items-start p-6 rounded-xl border-2 transition-all ${
                paymentType === "full"
                  ? "border-[var(--nba-green)] bg-[rgba(31,211,108,0.05)]"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => setPaymentType("full")}
              type="button"
            >
              <div className="flex-1 text-left">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-lg text-gray-900">
                    Full Payment
                  </span>
                  <div
                    className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                      paymentType === "full"
                        ? "border-[var(--nba-green)]"
                        : "border-gray-300"
                    }`}
                  >
                    {paymentType === "full" && (
                      <div className="w-3 h-3 rounded-full bg-[var(--nba-green)]" />
                    )}
                  </div>
                </div>
                <p className="text-gray-500">
                  Complete the transaction with one payment
                </p>
              </div>
            </button>

            {/* Installmental Payment Option */}
            <button
              className={`w-full flex items-start p-6 rounded-xl border-2 transition-all ${
                paymentType === "installment"
                  ? "border-[var(--nba-green)] bg-[rgba(31,211,108,0.05)]"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => setPaymentType("installment")}
              type="button"
            >
              <div className="flex-1 text-left">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-lg text-gray-900">
                    Installment Payment
                  </span>
                  <div
                    className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                      paymentType === "installment"
                        ? "border-[var(--nba-green)]"
                        : "border-gray-300"
                    }`}
                  >
                    {paymentType === "installment" && (
                      <div className="w-3 h-3 rounded-full bg-[var(--nba-green)]" />
                    )}
                  </div>
                </div>
                <p className="text-gray-500">
                  Split the payment into multiple installments
                </p>
              </div>
            </button>
          </div>

          {/* Actions */}
          <div className="mt-8">
            <button
              className="dashboard-button dashboard-button--primary dashboard-button--large w-full justify-center"
              type="button"
              onClick={handleContinue}
              disabled={!paymentType}
            >
              <span>Continue</span>
              <ArrowRight size={18} />
            </button>
          </div>

          {/* Provisional Summary Card */}
          <div className="mt-8 bg-gray-50 rounded-xl p-4 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center border border-gray-200 text-gray-400">
                <FileText size={24} />
              </div>
              <div>
                <div className="font-medium text-gray-900">
                  View Provisional Summary
                </div>
                <div className="text-sm text-gray-500">
                  Preview transaction details and timeline
                </div>
              </div>
            </div>
            <ArrowRight size={20} className="text-gray-400" />
          </div>

          {/* Info Box */}
          <div className="mt-6 flex gap-3 text-gray-500 text-sm bg-blue-50 p-4 rounded-lg">
            <Info size={20} className="text-blue-500 shrink-0" />
            <p>
              You'll receive email notifications for payment confirmation and
              certificate issuance. Client can pay via card, bank transfer, or
              USSD.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
